// 11 §7 — PARTY: one card per hero (mockup: portrait, name in the player's colour, class line, exact
// HP and HP ticks, slot pips 1 2 B empty / planned / locked). Hovering raises that hero's plans
// (§5.5 owner highlight); only heroes I control are selectable. docs/118 PT8–PT9: a ranger's companion
// has its own card right under its ranger's, and a double-click on any card eases the camera to that unit.
import { HeroSprite } from '../components';
import { useAuthoring } from '../../core/stores/authoring';
import { useView } from '../../core/stores/view';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { usePlayback } from '../../core/stores/playback';
import { useHeroCallings, useNames, useParty, useWorldHeroes } from '../../core/subscriptions/hooks';
import { besideRangers, COMPANION_KINDS, isCompanionId, rangerOf } from '../../core/sim/companion';
import { identityHex } from '../../core/net/connection';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { Ticks, useSeats } from './shared';
import { slotHints, slotMark } from './slots';
import { StatusChips } from './StatusChips';
import { useEncounterBoons } from '../../core/items/live';
import { BOONS } from '../../core/items/describe';

export function RosterStrip() {
  const selected = useAuthoring((s) => s.selectedHero);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const me = useAuthoring((s) => s.me);
  // docs/56: while a pulse plays the cards follow it (HP as each blow lands), not the next plan
  const playing = usePlayback((s) => s.playing);
  const planned = useAuthoring((s) => s.projected);
  const projected = playing ? undefined : planned;
  const select = useAuthoring((s) => s.select);
  const setOwnerHighlight = useView((s) => s.setOwnerHighlight);
  const setHoverHero = useView((s) => s.setHoverHero);
  const snapshot = useShownSnapshot();
  const owners = useEncounter((s) => s.owners);
  const worldId = useEncounter((s) => s.worldId);
  const partyId = useEncounter((s) => s.partyId);
  const party = useParty(partyId);
  const names = useNames(worldId);
  const heroRows = useWorldHeroes(worldId);
  const { colour } = useSeats();
  const frameUnit = useView((s) => s.frameUnit);
  const heroes = besideRangers((snapshot?.units ?? []).filter((u) => u.heroId !== undefined));
  const lead = party ? identityHex(party.lead) : undefined;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const callings = useHeroCallings(worldId);
  // docs/72 IT17: the boons each hero brought into this fight (from camp uses and meals)
  const encounterId = useEncounter((s) => s.encounterId);
  const boons = useEncounterBoons(encounterId);
  const describe = (heroId: number, fallback?: string) => {
    // docs/59 CC6: "Wolf · Teo's companion"
    if (isCompanionId(heroId)) {
      const ranger = rangerOf(heroId);
      const pick = callings.find((k) => Number(k.heroId) === ranger)?.pick ?? 0;
      const of = heroRows.find((h) => Number(h.id) === ranger)?.name;
      return [COMPANION_KINDS[pick] ?? 'Companion', of ? `${of}’s companion` : 'companion'].join(' · ');
    }
    const r = heroRows.find((h) => Number(h.id) === heroId); const cls = r ? CLASS_NAMES[r.class] ?? fallback : fallback; const sp = r ? SPECIES_NAMES[r.species] : undefined; return [cls, sp].filter(Boolean).join(' · ');
  };

  return (
    <section className="fk-hud__box fk-hud__party" aria-label="Roster" data-testid="party-roster">
      <h3 className="fk-hud__label">Party</h3>
      <ul className="fk-roster" role="list">
        {heroes.map((h) => {
          const owner = owners[h.heroId!] ?? '';
          const mine = owner === me;
          const pu = projected?.units.find((u) => u.unitId === h.unitId);
          const slots = slotHints(h.heroId!, local, rows).map((s) => {
            const row = rows.get(`${h.heroId}:${s}`);
            const l = local.get(`${h.heroId}:${s}`);
            return { slot: s, filled: !!row || !!l, committed: row?.state === 'committed' };
          });
          const unfilled = slots.filter((s) => !s.filled).length;
          const who = mine ? 'you' : names[owner] ?? owner.slice(-6);
          const cls = cap(describe(h.heroId!, h.classId).split(' · ')[0] || h.classId || 'hero');
          const sub = `${cls}${owner === lead ? ' · lead' : ''}${mine ? '' : ` · ${who}`}${h.downed ? ' · downed' : ''}`;
          const hpEnd = Math.max(0, pu ? pu.hpEnd : h.hp);
          const hpStart = pu ? pu.hpStart : h.hp;
          const beast = isCompanionId(h.heroId!);
          return (
            <li key={h.unitId}>
              <button type="button" className={`fk-roster__hero fk-hit ${mine ? 'fk-roster__hero--mine' : 'fk-roster__hero--theirs'} ${beast ? 'fk-roster__hero--companion' : ''} ${h.downed ? 'fk-roster__hero--downed' : ''}`} style={{ ['--fk-seat' as string]: colour(owner) }}
                aria-pressed={selected === h.heroId} data-testid="roster-hero" data-hero-name={h.name} data-mine={mine} data-companion={beast || undefined} title="Double-click to look at them"
                aria-label={`${h.name}, ${describe(h.heroId!, h.classId) || 'hero'}, ${mine ? 'yours' : `controlled by ${who}`}, ${unfilled} unfilled slot${unfilled === 1 ? '' : 's'}`}
                onClick={() => mine && select(h.heroId!)} onDoubleClick={() => frameUnit(h.unitId)}
                onMouseEnter={() => { setHoverHero(h.heroId!); setOwnerHighlight(owner); }} onMouseLeave={() => { setHoverHero(undefined); setOwnerHighlight(undefined); }}
                onFocus={() => setHoverHero(h.heroId!)} onBlur={() => setHoverHero(undefined)}>
                <span className="fk-roster__head">
                  <span className="fk-roster__face"><HeroSprite heroId={beast ? undefined : h.heroId} frame={h.appearance} classId={h.classId} scale={2} /></span>
                  <span className="fk-roster__id">
                    <span className="fk-roster__name" data-testid="hero-name">{h.name}{mine && <small> (you)</small>}</span>
                    <span className="fk-roster__sub" data-testid="roster-class" title={`${describe(h.heroId!, h.classId)} · ${who}`}>{sub}</span>
                  </span>
                </span>
                <span className="fk-roster__hp"><span>HP</span><b>{hpStart !== hpEnd ? <>{hpStart}<span className="fk-hud__arrow">→</span>{hpEnd}</> : hpEnd}/{h.hpMax}</b></span>
                <Ticks end={hpEnd} start={hpStart} max={h.hpMax} />
                <span className="fk-roster__foot">
                  <StatusChips statuses={h.statuses} />
                  {boons.filter((b) => Number(b.heroId) === h.heroId).map((b) => <span key={b.card} className="fk-roster__boon" title={`Boon: ${BOONS[b.card]?.name ?? b.card} — ${BOONS[b.card]?.text ?? ''}`} data-testid="roster-boon" data-card={b.card}>✦{BOONS[b.card]?.name ?? 'Boon'}</span>)}
                  <span className="fk-roster__dots" aria-hidden="true">
                    {slots.map((s) => <span key={String(s.slot)} className={`fk-roster__dot ${s.committed ? 'fk-roster__dot--lock' : s.filled ? 'fk-roster__dot--filled' : ''}`} title={`${s.slot === 'B' ? 'bonus' : `slot ${s.slot}`}${s.committed ? ' locked' : s.filled ? ' planned' : ' empty'}`}>{slotMark(s.slot)}</span>)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
